import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  signal,
  ViewChild,
  WritableSignal,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatStepper } from '@angular/material/stepper';
import { replaceAccents } from '@app/shared/utils';
import { Dispatch } from '@ngxs-labs/dispatch-decorator';
import { Actions, ofActionDispatched, Select, Store } from '@ngxs/store';
import { ProgramPlaylist, ProgramsState } from '@radioking/programs';
import { SchedulerCoreState } from '@radioking/scheduler-core';
import { MobileSidenavService } from '@radioking/shared/common-services';
import { SugestionInput } from '@radioking/ui/input';
import amplitude from 'amplitude-js';
import { BehaviorSubject, combineLatest, Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  ProgramAddPlaylistRequest,
  ProgramAddPlaylistSuccess,
} from '../../../states/programs.actions';

@Component({
  selector: 'rk-mob-program-add-playlist',
  templateUrl: './mob-program-add-playlist.component.html',
  styleUrls: ['./mob-program-add-playlist.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MobProgramAddPlaylistComponent implements OnInit, OnDestroy {
  addPlaylistForm: FormGroup;

  @ViewChild('stepper')
  stepper: MatStepper;

  searchSubject = new BehaviorSubject<string>('');

  $step: WritableSignal<number> = signal(1);
  totalSteps = 2;

  playlists$: Observable<SugestionInput[]>;

  @Select(SchedulerCoreState.allPlaylistsForDropdown)
  playlistsUnfiltered$: Observable<SugestionInput[]>;

  subscription = new Subscription();

  constructor(
    private readonly mobileService: MobileSidenavService,
    private readonly store: Store,
    private readonly actions$: Actions,
  ) {}

  ngOnInit() {
    this.playlists$ = combineLatest([
      this.playlistsUnfiltered$,
      this.searchSubject.asObservable(),
    ]).pipe(
      map(([playlists, search]) => {
        const searchTerm = replaceAccents(search.toLowerCase());
        return playlists.filter(pl =>
          replaceAccents(pl.name.toLowerCase()).includes(searchTerm),
        );
      }),
    );

    this.addPlaylistForm = new FormBuilder().group({
      selectedPlaylist: [undefined, Validators.required],
      sorting: 'Sequential',
      tracksNumber: [1, Validators.required],
    });

    this.subscription.add(
      this.actions$.pipe(ofActionDispatched(ProgramAddPlaylistSuccess)).subscribe(() => {
        this.mobileService.close();
      }),
    );
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  onChangeSearch(str: string) {
    this.searchSubject.next(str);
  }

  cancel() {
    this.mobileService.close();
  }

  nextStep() {
    this.$step.update(v => v + 1);
    this.stepper.next();
  }

  prevStep() {
    this.$step.update(v => v - 1);
    this.stepper.previous();
  }

  selectPlaylist(playlist: SugestionInput) {
    this.addPlaylistForm.get('selectedPlaylist').setValue(playlist);
    this.nextStep();
  }

  @Dispatch()
  submit() {
    const program = this.store.selectSnapshot(ProgramsState.programDetails);
    amplitude.getInstance().logEvent('add_playlist_program');
    const formValues = this.addPlaylistForm.value;
    const pPlaylist: Partial<ProgramPlaylist> = {
      idPlaylist: formValues.selectedPlaylist.id,
      tracksNumber: formValues.tracksNumber,
      sorting: formValues.sorting,
      position: program.playlistsNumber + 1,
    };

    return new ProgramAddPlaylistRequest(program.id, pPlaylist);
  }
}
